import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { BASE_URL } from '../../services/Api';
import { authHeader } from '../../services/auth-header';

function CommonDcFields({ onDataChange, initialData }) {
    // Customer related states
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerSearch, setCustomerSearch] = useState(" ");
    const [customer, setCustomer] = useState(initialData?.customer || null);
    const [isCustomerOpen, setIsCustomerOpen] = useState(false);

    const [customerDetails, setCustomerDetails] = useState({
        contactperson: initialData?.customerDetails?.contactperson || '',
        contactno: initialData?.customerDetails?.contactno || '',
        email: initialData?.customerDetails?.email || '',
        freight: initialData?.customerDetails?.freight || '',
        packingandforwarding: initialData?.customerDetails?.packingandforwarding || '',
        paymentterm: initialData?.customerDetails?.paymentterm || ''
    });

    // Address related states
    const [customerAddresses, setCustomerAddresses] = useState([]);
    const [customerMultipleAddress, setCustomerMultipleAddress] = useState(initialData?.customerMultipleAddress || null);
    const [shipTo, setShipTo] = useState(initialData?.shipTo || null);
    const [billTo, setBillTo] = useState(initialData?.billTo || null);

    // Branch/Warehouse states
    const [branchOptions, setBranchOptions] = useState([]);
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [branch, setBranch] = useState(initialData?.branch || null);
    const [warehouse, setWarehouse] = useState(initialData?.warehouse || null);
    const [isBranchWarehouseOpen, setIsBranchWarehouseOpen] = useState(false);
    const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false);

    const [remarks, setremarks] = useState(initialData?.remarks || '');

    // Memoized fetch functions
    const getBranchData = useCallback(async () => {
        try {
            if (branchOptions.length === 0) {
                const response = await axios.get(`${BASE_URL}branch/findAllBranch`, {
                    headers: authHeader(),
                });
                setBranchOptions(response.data.map(b => ({
                    value: b.id,
                    label: b.branchname
                })));
            }
        } catch (error) {
            console.error("Error fetching branches", error);
        }
    }, [branchOptions.length]);

    const getWarehouseData = useCallback(async () => {
        try {
            if (warehouseOptions.length === 0) {
                const response = await axios.get(`${BASE_URL}warehouse/findAllWarehouse`, {
                    headers: authHeader(),
                });
                setWarehouseOptions(response.data.map(w => ({
                    value: w.id,
                    label: w.warehousename
                })));
            }
        } catch (error) {
            console.error("Error fetching warehouses", error);
        }
    }, [warehouseOptions.length]);

    const getCustomerAddresses = useCallback(async (id) => {
        try {
            const response = await axios.get(`${BASE_URL}customerMultipleAddress/getAddress/${id}`, {
                headers: authHeader(),
            });

            const addresses = response.data.map(addr => ({
                value: addr.id,
                label: addr.customeraddress,
            }));

            setCustomerAddresses(addresses);

            // Match initial addresses if they exist
            if (initialData?.shipTo?.value) {
                const foundShipTo = addresses.find(addr => addr.value === initialData.shipTo.value);
                if (foundShipTo) setShipTo(foundShipTo);
            }

            if (initialData?.billTo?.value) {
                const foundBillTo = addresses.find(addr => addr.value === initialData.billTo.value);
                if (foundBillTo) setBillTo(foundBillTo);
            }
        } catch (error) {
            console.error("Error fetching customer addresses", error);
        }
    }, [initialData]);

    // Initial data load - runs only once
    useEffect(() => {
        if (!hasFetchedInitialData) {
            if (initialData?.customer?.value) {
                getCustomerAddresses(initialData.customer.value);
            }

            getBranchData();
            getWarehouseData();

            setHasFetchedInitialData(true);
        }
    }, [initialData, hasFetchedInitialData, getBranchData, getWarehouseData, getCustomerAddresses]);

    // Customer search with debounce
    const handleSearchChangeCustomer = useCallback(
        debounce((inputValue) => {
            setCustomerSearch(inputValue || " ");
        }, 300),
        []
    );

    // Fetch customer data when search term changes
    const getCustomerData = useCallback(async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}customer/page/0/10/asc/id/${encodeURIComponent(customerSearch)}`,
                { headers: authHeader() }
            );

            const customers = response.data.Index || response.data.data || [];
            setCustomerOptions(
                customers.map((s) => ({
                    value: s.id,
                    label: s.companyname,
                    contactperson: s.contactperson,
                    contactno: s.contactno,
                    email: s.email,
                    freight: s.freight,
                    packingandforwarding: s.packingandforwarding,
                    paymentterm: s.paymentterm,
                }))
            );
        } catch (error) {
            console.error("Error fetching customer data", error);
        }
    }, [customerSearch]);

    useEffect(() => {
        getCustomerData();
    }, [getCustomerData]);

    // Handle customer selection
    const handleCustomerChange = useCallback((selected) => {
        setCustomer(selected);
        if (selected) {
            setCustomerDetails({
                contactperson: selected.contactperson,
                contactno: selected.contactno,
                email: selected.email,
                freight: selected.freight,
                packingandforwarding: selected.packingandforwarding,
                paymentterm: selected.paymentterm,
            });
            getCustomerAddresses(selected.value);
            setIsCustomerOpen(true);
        } else {
            setCustomerDetails({
                contactperson: '',
                contactno: '',
                email: '',
                freight: '',
                packingandforwarding: '',
                paymentterm: ''
            });
            setCustomerAddresses([]);
            setShipTo(null);
            setBillTo(null);
        }
        setCustomerMultipleAddress(null);
    }, [getCustomerAddresses]);

    // Handle data changes and notify parent
    const handleDataUpdate = useCallback(() => {
        if (onDataChange) {
            onDataChange({
                customer,
                customerDetails,
                customerMultipleAddress,
                branch,
                warehouse,
                shipTo,
                billTo,
                remarks
            });
        }
    }, [onDataChange, customer, customerDetails, customerMultipleAddress, branch, warehouse, shipTo, billTo, remarks]);

    useEffect(() => {
        handleDataUpdate();
    }, [handleDataUpdate]);

    // Toggle sections
    const toggleCustomerSection = () => setIsCustomerOpen(!isCustomerOpen);
    const toggleBranchWarehouseSection = () => setIsBranchWarehouseOpen(!isBranchWarehouseOpen);

    // Handle field changes
    const handleCustomerDetailsChange = (field, value) => {
        setCustomerDetails(prev => ({ ...prev, [field]: value }));
    };

    const handleBranchChange = (selected) => setBranch(selected);
    const handleWarehouseChange = (selected) => setWarehouse(selected);
    const handleShipToChange = (selected) => setShipTo(selected);
    const handleBillToChange = (selected) => setBillTo(selected);

    return (
        <>
            <br />

            <div className="supplier-accordion-container">
                <div
                    className={`supplier-accordion-header ${isCustomerOpen ? 'supplier-accordion-open' : ''}`}
                    onClick={toggleCustomerSection}
                >
                    <h7>Customer Information</h7>
                    <span className="supplier-accordion-icon">
                        {isCustomerOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`supplier-accordion-content ${isCustomerOpen ? 'supplier-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Customer</Form.Label>
                                <Select
                                    options={customerOptions}
                                    onChange={handleCustomerChange}
                                    onInputChange={handleSearchChangeCustomer}
                                    value={customer}
                                    placeholder="Select customer"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Contact Person</Form.Label>
                                <Form.Control
                                    value={customerDetails.contactperson}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Phone</Form.Label>
                                <Form.Control
                                    value={customerDetails.contactno}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    value={customerDetails.email}
                                    disabled
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Customer Address</Form.Label>
                                <Select
                                    options={customerAddresses}
                                    value={customerMultipleAddress}
                                    onChange={(val) => setCustomerMultipleAddress(val)}
                                    isClearable
                                    placeholder="Select address"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Ship To:</Form.Label>
                                <Select
                                    options={customerAddresses}
                                    placeholder="Select Ship To Address"
                                    onChange={handleShipToChange}
                                    value={shipTo}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Bill To:</Form.Label>
                                <Select
                                    options={customerAddresses}
                                    placeholder="Select Bill To Address"
                                    onChange={handleBillToChange}
                                    value={billTo}
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Freight</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={customerDetails.freight}
                                    onChange={e => handleCustomerDetailsChange('freight', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Packing & Forwarding</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={customerDetails.packingandforwarding}
                                    onChange={e => handleCustomerDetailsChange('packingandforwarding', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group>
                                <Form.Label>Payment Term</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={customerDetails.paymentterm}
                                    onChange={e => handleCustomerDetailsChange('paymentterm', e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Branch & Warehouse Section */}
            <div className="supplier-accordion-container">
                <div
                    className={`supplier-accordion-header ${isBranchWarehouseOpen ? 'supplier-accordion-open' : ''}`}
                    onClick={toggleBranchWarehouseSection}
                >
                    <h7 className="mb-0">Branch & Warehouse Details</h7>
                    <span className="supplier-accordion-icon">
                        {isBranchWarehouseOpen ? '−' : '+'}
                    </span>
                </div>

                <div className={`supplier-accordion-content ${isBranchWarehouseOpen ? 'supplier-accordion-content-show' : ''}`}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Branch</Form.Label>
                                <Select
                                    value={branch}
                                    onChange={handleBranchChange}
                                    options={branchOptions}
                                    placeholder="Select branch"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Warehouse</Form.Label>
                                <Select
                                    value={warehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouseOptions}
                                    placeholder="Select warehouse"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </div>
            </div>
            <br />
            <Row>
                <Col md={12}>
                    <Form.Group>
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                            type="text"
                            value={remarks}
                            onChange={(e) => setremarks(e.target.value)}
                        />
                    </Form.Group>
                </Col>
            </Row>
        </>
    );
}

export default CommonDcFields;